import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SpinnerComponent } from 'src/app/components/layout/spinner/spinner.component';
import { FaqService } from 'src/app/services/faq/faq.service';
import { FaqItem, FaqPageItem, Lang } from 'src/app/models/faq';
import { FaqModalComponent } from './faq-modal/faq-modal.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { LanguageService } from 'src/app/services/language/language.service';
import { Subject, startWith, takeUntil } from 'rxjs';

@Component({
  selector: 'app-faq',
  standalone: true,
  imports: [CommonModule, FormsModule, SpinnerComponent, DragDropModule],
  templateUrl: './faq.component.html',
  styleUrls: ['./faq.component.css']
})
export class FaqComponent implements OnInit, OnDestroy {
  lang: Lang = 'geo';

  loading = false;
  pagesLoading = false;

  pages: FaqPageItem[] = [];
  selectedMenuPage: number | null = null;

  items: FaqItem[] = [];

  search = '';
  visible: 'all' | '1' | '0' = 'all';

  togglingId: number | null = null;
  deletingIds = new Set<number>();
  sorting = false;
  private destroy$ = new Subject<void>();

  constructor(
    private api: FaqService,
    private modal: NgbModal,
    private langService: LanguageService
  ) {}

  ngOnInit(): void {
    // initial lang from storage/service
    this.lang = this.langService.getLanguage() as Lang;

    // Load immediately (so page doesn't stay empty)
    this.loadPages(true);

    // React to language changes; startWith ensures it runs even if currentLanguage$ doesn't emit immediately
    this.langService.currentLanguage$
      .pipe(
        startWith(this.lang),
        takeUntil(this.destroy$)
      )
      .subscribe((lang: any) => {
        const nextLang = (lang ?? this.lang) as Lang;
        if (nextLang === this.lang) return;

        this.lang = nextLang;
        // reset selection/items when language changes
        this.selectedMenuPage = null;
        this.items = [];
        this.loadPages(true);
      });
  }

  loadPages(autoSelectAndLoadFaqs: boolean = false) {
    this.pagesLoading = true;
    this.api.getFaqPages(this.lang).subscribe({
      next: (res) => {
        this.pages = res.data ?? [];
        this.pagesLoading = false;

        if (!autoSelectAndLoadFaqs) return;

        // Keep current selection if it exists in new list
        const stillExists = this.selectedMenuPage && this.pages.some(p => p.menu_page === this.selectedMenuPage);

        if (!stillExists) {
          this.selectedMenuPage = this.pages.length ? this.pages[0].menu_page : null;
        }

        this.get();
      },
      error: () => {
        this.pagesLoading = false;
        this.pages = [];
        if (autoSelectAndLoadFaqs) {
          this.selectedMenuPage = null;
          this.items = [];
        }
      }
    });
  }

  onPageChange() {
    this.search = '';
    this.visible = 'all';
    this.get();
  }

  get() {
    if (!this.selectedMenuPage) {
      this.items = [];
      return;
    }

    this.loading = true;
    this.api.getFaqs(this.lang, this.selectedMenuPage, this.search, this.visible).subscribe({
      next: (res) => {
        this.items = (res.data ?? []).slice().sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
        this.loading = false;
      },
      error: () => (this.loading = false)
    });
  }

  onSearchChange() {
    this.get();
  }

  openCreate() {
    if (!this.selectedMenuPage) return;

    const ref = this.modal.open(FaqModalComponent, { size: 'lg', scrollable: true });
    ref.componentInstance.lang = this.lang;
    ref.componentInstance.pages = this.pages;
    ref.componentInstance.selectedMenuPage = this.selectedMenuPage;

    ref.result.then(
      () => this.get(),
      () => {}
    );
  }

  openEdit(item: FaqItem) {
    const ref = this.modal.open(FaqModalComponent, { size: 'lg', scrollable: true });
    ref.componentInstance.lang = this.lang;
    ref.componentInstance.pages = this.pages;
    ref.componentInstance.faqId = item.id;
    ref.componentInstance.selectedMenuPage = this.selectedMenuPage;

    ref.result.then(
      () => this.get(),
      () => {}
    );
  }

  toggleVisible(item: FaqItem) {
    if (this.togglingId) return;
    this.togglingId = item.id;

    this.api.toggleVisible(item.id, this.lang).subscribe({
      next: (res) => {
        item.visible = res.visible ?? item.visible;
        this.togglingId = null;
      },
      error: () => (this.togglingId = null)
    });
  }

  delete(item: FaqItem) {
    if (!confirm('წავშალო?')) return;
    this.deletingIds.add(item.id);

    this.api.delete(item.id, this.lang, true).subscribe({
      next: () => this.get(),
      error: () => this.deletingIds.delete(item.id)
    });
  }

  drop(ev: CdkDragDrop<FaqItem[]>) {
    if (!this.selectedMenuPage) return;

    moveItemInArray(this.items, ev.previousIndex, ev.currentIndex);

    const ids = this.items.map(x => x.id);

    this.sorting = true;
    this.api.sort(this.lang, this.selectedMenuPage, ids).subscribe({
      next: () => (this.sorting = false),
      error: () => (this.sorting = false)
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}